import Autocomplete from '@mui/material/Autocomplete'
import { Form } from 'components/collections'
import { Loading, Tag, Input } from 'components/elements'
import { useDebounce } from 'components/helpers/utils'
import { getAlphaRgb } from 'components/layouts'
import { useFormikContext } from 'formik'
import * as R from 'ramda'
import React, { useState } from 'react'
import { MdOutlinePhoto, MdClose } from 'react-icons/md'
import { useQuery } from 'react-query'
import styled, { css } from 'styled-components'

import { loadTags } from '../../modules/datasource'

export const FormTitle = styled.div`
  ${({ theme }) => theme.font.h3}
  margin-bottom: 12px;
`
export const FormHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 32px;

  ${({ theme }) => theme.media.phone} {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 72px 64px;
  ${({ theme }) => theme.media.phone} {
    padding: 10px 16px 64px;
  }
`
export const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: auto;
  width: calc(100% - 150px);
  ${({ theme }) => theme.media.phone} {
    margin-bottom: 24px;
    width: 100%;
  }
`
export const PrivacySelect = styled(Form.Select)`
  max-width: 120px;
  ${({ theme }) => theme.media.phone} {
    max-width: 100%;
  }
`

export const ImageContainer = styled.div`
  width: 100%;
  max-width: 416px;
  display: flex;
  flex-direction: column;
  div {
    ${({ theme }) => theme.font.label}
  }
`
export const ImgPreview = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  height: 64px;
  width: 64px;
  min-height: 64px;
  min-width: 64px;
  background: ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  margin-bottom: 8px;
  ${({ src }) =>
    src &&
    css`
      background-image: ${`url(${src})`};
      background-size: cover;
      background-position: center;
      &:hover {
        &::after {
          content: 'Edit';
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: ${({ theme }) => getAlphaRgb(theme.colors.grey, 0.5)};
          color: ${({ theme }) => theme.colors.white};
        }
      }
    `};

  ${({ theme }) => theme.media.phone} {
    width: 100%;
    height: 193px;
    max-width: unset;
  }
`

const sectionsBorder = css`
  border-top: solid 1px ${({ theme }) => theme.colors.grey};
`
export const FormSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  ${sectionsBorder}
`

export const FormFooter = styled.div`
  display: flex;
  flex-direction: column;
`

export const PostButtonContainer = styled.div`
  display: flex;
  margin-top: 24px;
  ${({ theme }) => theme.media.phone} {
    width: 100%;
  }
`

export const PhotoIcon = styled(MdOutlinePhoto)`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 12px;
`
export const CloseIcon = styled(MdClose)`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 12px;
`
const TagsSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  ${sectionsBorder}
`
const TagsTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 16px;
`
const TagsContent = styled.div`
  display: flex;
  .MuiAutocomplete-root {
    width: 200px;
  }
  .MuiOutlinedInput-root {
    padding: 7px;
  }

  .MuiInputLabel-outlined.MuiInputLabel-marginDense {
    transform: translate(14px, 18px) scale(1);
  }
  .MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: translate(14px, -6px) scale(0.75);
  }
  ${({ theme }) => theme.media.phone} {
    flex-direction: column;
  }
`
const TagInput = styled(Input)``

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  margin: -4px 16px;
  ${({ theme }) => theme.media.phone} {
    margin: 16px -4px 0;
  }
`
const TagItem = styled(Tag)`
  margin: 4px;
`
export const Tags = ({ isFetching, accessToken }) => {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const { values, setFieldValue } = useFormikContext()
  const { tags = [] } = values

  // const tagsList = useTags(accessToken, inputValue)

  const debouncedSearchTerm = useDebounce(inputValue, 500)

  const { data: tagsData = {}, isLoading } = useQuery(
    ['tags', { accessToken, debouncedSearchTerm }],
    () => loadTags({ accessToken, name: debouncedSearchTerm }),
    {
      fetchPolicy: 'no-cache',
    }
  )

  const { list: tagsList = [] } = tagsData

  return (
    <TagsSection>
      <TagsTitle>
        Add up to 3 tags to help reader know what it’s about
      </TagsTitle>
      <TagsContent>
        <Autocomplete
          size="small"
          disabled={isFetching || R.length(tags) >= 3}
          multiple
          open={open}
          loading={isLoading}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          disableClearable
          renderTags={() => null}
          value={tags}
          isOptionEqualToValue={(option, value) => {
            return option?.id === value?.id
          }}
          onChange={(_, value) => {
            setFieldValue('tags', value)
          }}
          options={tagsList}
          noOptionsText="Type to search"
          renderInput={params => (
            <TagInput
              {...params}
              label="Tag"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <Loading size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue)
          }}
          getOptionLabel={o => o?.name}
        />
        {!R.isEmpty(tags) && (
          <TagList>
            {tags.map(item => (
              <TagItem
                disabled={isFetching}
                key={`tag-${item?.name}`}
                label={item?.name}
                onRemove={() => setFieldValue('tags', R.without([item], tags))}
              />
            ))}
          </TagList>
        )}
      </TagsContent>
    </TagsSection>
  )
}
